import React, { useMemo, useRef } from 'react';
import { Link, Redirect, Route, useParams } from 'react-router-dom';
import { Box, ListItemText, Tab, Tabs } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Warning as WarningIcon } from '@material-ui/icons';

import ErrorView from '../../common/views/ErrorView';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateNav from '../../common/page-template/PageTemplateNav';
import PageTemplateHeader from '../../common/page-template/PageTemplateHeader';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import { Role } from '../../user/User.interface';
import { useUserContext } from '../../user/User.context';
import { TrainingPageContent } from '../training/TrainingPage';
import { AdminDashboardPageContent } from '../admin-dashboard/AdminDashboardPage';
import DashboardAssignedTab from './components/DashboardAssignedTab';
import { useDashboardPendingItems } from './Dashboard.hooks';

type PageType = 'assigned' | 'training' | 'overview';

const DashboardPage: React.FC = () => {
  const { user } = useUserContext();
  const { view } = useParams<{ view?: PageType }>();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { hasTrainingItems, hasPendingItems } = useDashboardPendingItems();
  const noRoles: boolean = user?.roles?.length === 0;

  const isAdmin: boolean = useMemo(() => {
    return !!user?.roles?.some((role: Role) => {
      return role.name && ['Admin', 'SiteAdmin'].includes(role.name);
    });
  }, [user]);

  const allowedPages: PageType[] = useMemo(() => {
    const pages: PageType[] = ['assigned', 'training'];
    isAdmin && pages.push('overview');
    return pages;
  }, [isAdmin]);

  const getPageUrl = (page: PageType) => {
    return `/portal/dashboard/${page}`;
  };

  if (!view || !allowedPages.includes(view)) {
    return <Redirect to='/portal/dashboard/assigned' />;
  }
  return (
    <PageTemplate>
      <PageTemplateHeader>
        <h2>Dashboard</h2>
      </PageTemplateHeader>
      <PageTemplateNav tabs>
        <Tabs
          value={view}
          variant='scrollable'
          scrollButtons='auto'
          indicatorColor='primary'
        >
          <Tab
            component={Link}
            label='Assigned to Me'
            value='assigned'
            to={getPageUrl('assigned')}
          />
          <Tab
            component={Link}
            label='My Training'
            value='training'
            to={getPageUrl('training')}
          />
          {isAdmin && (
            <Tab
              component={Link}
              label='QMS Overview'
              value='overview'
              to={getPageUrl('overview')}
            />
          )}
        </Tabs>
      </PageTemplateNav>
      {noRoles ? (
        <ErrorView
          title='Account Setup Incomplete'
          description='Please contact your administrator to update your account permissions to be able to proceed.'
          icon={<WarningIcon color='disabled' fontSize='inherit' />}
        />
      ) : (
        <PageTemplateDashboard innerRef={scrollRef}>
          <Box mb={hasPendingItems ? 1 : 2}>
            <ListItemText
              primary={`Welcome back ${user?.firstName || '?'}!`}
              secondary="Here's what's on your plate today."
              primaryTypographyProps={{
                color: 'primary',
                style: { fontWeight: 'bold' }
              }}
            />
          </Box>
          {hasTrainingItems && (
            <Box mb={2}>
              <Alert severity='warning'>
                You have pending training tasks.
                {view !== 'training' && (
                  <>
                    {' '}
                    <Link to='/portal/dashboard/training'>
                      View My Training Dashboard
                    </Link>
                  </>
                )}
              </Alert>
            </Box>
          )}

          <Route exact path='/portal/dashboard/assigned'>
            <DashboardAssignedTab />
          </Route>
          <Route exact path='/portal/dashboard/training'>
            <TrainingPageContent />
          </Route>
          <Route exact path='/portal/dashboard/overview'>
            <AdminDashboardPageContent />
          </Route>
        </PageTemplateDashboard>
      )}
    </PageTemplate>
  );
};

export default DashboardPage;
